import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    ChevronLeftIcon, 
    UserIcon, 
    MagnifyingGlassIcon,
    ArrowPathIcon,
    CheckCircleIcon,
    ChatBubbleBottomCenterTextIcon,
    ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import BlueprintCreator from '../../components/teacher/BlueprintCreator';
import ConfirmModal from '../../components/common/ConfirmModal';
import { calculateLevel, calculate8Level } from '../../utils/cbcUtils';


const GradeAssessmentPage = () => {
    const { courseId, assignmentId } = useParams();
    const navigate = useNavigate();
    const { get, patch } = useApi();
    const { showToast, performanceBands } = useAppState();

    const isAcademic = !!courseId;

    const [loading, setLoading] = useState(true);
    const [assignment, setAssignment] = useState(null);
    const [students, setStudents] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStudentId, setSelectedStudentId] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isForgingTemplate, setIsForgingTemplate] = useState(false);

    // Template Change Confirmation State
    const [pendingTemplate, setPendingTemplate] = useState(null);
    const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);

    // Grading state
    const [grades, setGrades] = useState({});
    const [rawMarks, setRawMarks] = useState({});
    const [strandScores, setStrandScores] = useState({});
    const [teacherComments, setTeacherComments] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [isMovingNext, setIsMovingNext] = useState(false);
    const savingIds = useRef(new Set());

    const competencyLevels = [
        { code: 'EE', label: 'Exceeding Expectations', color: 'gold', bg: 'bg-[#FFD700]/5', text: 'text-[#FFD700]', border: 'border-[#FFD700]/40' },
        { code: 'ME', label: 'Meeting Expectations', color: 'turquoise', bg: 'bg-[#40E0D0]/5', text: 'text-[#40E0D0]', border: 'border-[#40E0D0]/40' },
        { code: 'AE', label: 'Approaching Expectations', color: 'coral', bg: 'bg-[#E97451]/5', text: 'text-[#E97451]', border: 'border-[#E97451]/40' },
        { code: 'BE', label: 'Below Expectations', color: 'grey', bg: 'bg-[#808080]/5', text: 'text-[#808080]', border: 'border-[#808080]/40' }
    ];

    const currentStudent = useMemo(() => 
        students.find(s => s.student === selectedStudentId), 
    [students, selectedStudentId]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            
            const assignmentUrl = isAcademic 
                ? `/api/assignments/${assignmentId}/`
                : `/api/events/club-assignments/${assignmentId}/`;
            
            const submissionsUrl = isAcademic
                ? `/api/assignments/${assignmentId}/submissions/`
                : `/api/events/club-assignments/${assignmentId}/submissions/`;

            const [assignmentData, submissionsData] = await Promise.all([
                get(assignmentUrl),
                get(submissionsUrl)
            ]);
            const sortedSubmissions = (submissionsData || []).sort((a, b) => 
                (a.student_name || "").localeCompare(b.student_name || "")
            );
            
            setAssignment(assignmentData);
            setStudents(sortedSubmissions);
            
            // Set initial grading states
            const initialGrades = {};
            const initialMarks = {};
            const initialStrandScores = {};
            const initialComments = {};
            sortedSubmissions.forEach(sub => {
                const sId = sub.student;
                initialGrades[sId] = sub.competency_level || '';
                initialMarks[sId] = sub.score || sub.grade || '';
                initialComments[sId] = sub.feedback || '';
                if (sub.marking_data && sub.marking_data.strand_scores) {
                    initialStrandScores[sId] = sub.marking_data.strand_scores;
                }
            });
            setGrades(initialGrades);
            setRawMarks(initialMarks);
            setStrandScores(initialStrandScores);
            setTeacherComments(initialComments);

            if (sortedSubmissions.length > 0 && !selectedStudentId) {
                setSelectedStudentId(sortedSubmissions[0].student);
            }

            // Fetch templates
            const templateQuery = isAcademic 
                ? `learning_area_id=${assignmentData.learning_area}`
                : `club_id=${assignmentData.club}`;
            const templatesData = await get(`/api/cbc/templates/by_learning_area/?${templateQuery}`);
            setTemplates(templatesData || []);

        } catch (err) {
            console.error('Error fetching data:', err);
            showToast('Failed to load assessment details', 'error');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignmentId, get, showToast, isAcademic]);

    useEffect(() => {
        fetchData();
    }, [assignmentId, fetchData]);

    useEffect(() => {
        if (selectedStudentId && students.length > 0 && templates.length > 0) {
            const studentSub = students.find(s => s.student === selectedStudentId);
            if (studentSub?.marking_data?.templateId) {
                const savedTemplate = templates.find(t => String(t.id) === String(studentSub.marking_data.templateId));
                if (savedTemplate) {
                    setSelectedTemplate(savedTemplate);
                    return; // Found and restored, exit effect
                }
            }

            // Fallback: If no student-specific template, use assignment defaults
            if (assignment?.rubric_template) {
                const matched = templates.find(t => String(t.id) === String(assignment.rubric_template));
                if (matched) setSelectedTemplate(matched);
                else setSelectedTemplate(null);
            } else if (assignment?.rubric_templates && assignment.rubric_templates.length > 0) {
                const matched = templates.find(t => String(t.id) === String(assignment.rubric_templates[0]));
                if (matched) setSelectedTemplate(matched);
                else setSelectedTemplate(null);
            } else {
                const matched = templates.find(t => 
                    t.criteria_ee === assignment?.criteria_ee &&
                    t.criteria_me === assignment?.criteria_me &&
                    t.criteria_ae === assignment?.criteria_ae &&
                    t.criteria_be === assignment?.criteria_be
                );
                if (matched) setSelectedTemplate(matched);
            }
        }
    }, [selectedStudentId, students, templates, assignment]);

    const filteredStudents = students.filter(s => 
        s.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.student_registration_id?.toString().includes(searchTerm) ||
        s.student_id?.toString().includes(searchTerm) ||
        s.student?.toString().includes(searchTerm)
    ).sort((a, b) => (a.student_name || "").localeCompare(b.student_name || ""));

    const handleTemplateConfirm = async () => {
        if (!pendingTemplate) return;
        setIsUpdatingTemplate(true);
        try {
            const patchUrl = isAcademic 
                ? `/api/assignments/${assignmentId}/`
                : `/api/events/club-assignments/${assignmentId}/`;
            
            await patch(patchUrl, { rubric_templates: [pendingTemplate.id] });
            setSelectedTemplate(pendingTemplate);
            setAssignment(prev => ({ ...prev, rubric_templates: [pendingTemplate.id] }));
            showToast('Assessment template updated successfully', 'success');
        } catch (error) {
            console.error('Error updating template:', error);
            showToast('Failed to update assessment template', 'error');
        } finally {
            setIsUpdatingTemplate(false);
            setPendingTemplate(null);
        }
    };

    const handleRawMarkChange = (studentId, mark) => {
        const numericMark = parseFloat(mark) || 0;
        const totalMarks = assignment?.total_marks || 100;
        const percentage = (numericMark / totalMarks) * 100;

        // Use the unified 4-level logic for UI highlighting (EE, ME, AE, BE)
        calculate8Level(percentage, performanceBands); // Keep 8-level check if needed
        const displayLevel = calculateLevel(numericMark, totalMarks, performanceBands); // Use 4-level for button highlighting

        setRawMarks(prev => ({ ...prev, [studentId]: mark }));
        setGrades(prev => ({ ...prev, [studentId]: displayLevel }));
    };

    const handleStrandInputChange = (strandId, val, strandMax) => {
        const studentId = selectedStudentId;
        const totalMarks = assignment?.total_marks || 100;
        
        let newStrandVal = val;
        if (val !== '') {
            const parsed = parseFloat(val);
            if (!isNaN(parsed)) {
                newStrandVal = Math.max(0, Math.min(strandMax, Math.round(parsed)));
            }
        }

        const currentStudentStrands = {
            ...(strandScores[studentId] || {}),
            [strandId]: newStrandVal
        };

        setStrandScores(prev => ({
            ...prev,
            [studentId]: currentStudentStrands
        }));

        // Auto-sum explicit strand scores for overall total mark
        let sumTotal = 0;
        let hasAnyValue = false;
        Object.values(currentStudentStrands).forEach(score => {
            if (score !== '' && score !== null && score !== undefined) {
                sumTotal += parseFloat(score) || 0;
                hasAnyValue = true;
            }
        });

        if (hasAnyValue) {
            const roundedTotal = Math.round(sumTotal);
            const newDisplayLevel = calculateLevel(roundedTotal, totalMarks, performanceBands);
            setRawMarks(prev => ({ ...prev, [studentId]: roundedTotal.toString() }));
            setGrades(prev => ({ ...prev, [studentId]: newDisplayLevel }));
        } else {
            setRawMarks(prev => ({ ...prev, [studentId]: '' }));
            setGrades(prev => ({ ...prev, [studentId]: '' }));
        }
    };

    const saveGrade = async (studentId, level, score, feedback, existingMarkingData = {}) => {
        // Prevent simultaneous parallel requests for the same student (The Root Cause)
        if (savingIds.current.has(studentId)) {
            console.log(`Save already in flight for student ${studentId}, skipping redundant call.`);
            return false;
        }

        try {
            savingIds.current.add(studentId);
            setSubmitting(true);
            const submission = students.find(s => s.student === studentId);
            if (!submission) return false;

            const patchUrl = isAcademic
                ? `/courses/assignment-submissions/${submission.id}/`
                : `/api/events/club-submissions/${submission.id}/`;

            const studentStrands = strandScores[studentId] || {};

            // Prepare payload
            const payload = {
                status: 'graded',
                competency_level: level,
                feedback: feedback || "Assessed via Grading Page",
                marking_data: {
                    ...(existingMarkingData || {}),
                    strand_scores: studentStrands,
                    templateId: selectedTemplate?.id
                }
            };

            // Fix persistence by using the correct backend field for academic mode
            if (isAcademic) {
                payload.grade = score;
            } else {
                payload.score = score;
            }

            await patch(patchUrl, payload);

            // Update local student status with fresh data
            setStudents(prev => prev.map(s => 
                s.student === studentId ? { 
                    ...s, 
                    status: 'graded', 
                    competency_level: level, 
                    grade: isAcademic ? score : s.grade,
                    score: !isAcademic ? score : s.score,
                    marking_data: payload.marking_data
                } : s
            ));
            
            showToast(`Grade saved for ${submission.student_name.split(' ')[0]}`, 'success');
            return true;
        } catch (err) {
            console.error('Error saving grade:', err);
            showToast('Failed to save grade.', 'error');
            return false;
        } finally {
            savingIds.current.delete(studentId);
            setSubmitting(false);
        }
    };

    if (loading && !assignment) return <LoadingSpinner fullScreen message="Loading Assessment..." />;

    return (
        <div className="h-screen bg-slate-50 flex flex-col overflow-hidden">
            {/* Top Navigation Bar */}
            <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shrink-0 z-[60] shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(isAcademic ? `/teacher/courses/${courseId}?tab=assignments` : '/teacher/clubs')}
                        className="p-2.5 hover:bg-slate-100 rounded-full transition-all group"
                    >
                        <ChevronLeftIcon className="w-5 h-5 text-slate-600 group-hover:-translate-x-0.5 transition-transform" />
                    </button>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                            <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-md text-[9px] font-black uppercase tracking-widest border border-indigo-100">
                                {isAcademic ? (assignment?.learning_area_name || 'Academic') : (assignment?.club_name || 'Extracurricular')}
                            </span>
                        </div>
                        <h1 className="text-lg font-black text-[#18216D] truncate max-w-[200px] md:max-w-xl uppercase tracking-tight italic">
                            {assignment?.title}
                        </h1>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-xl border border-slate-100">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                        <span className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">
                            {students.filter(s => s.status === 'graded').length} / {students.length} Evaluated
                        </span>
                    </div>
                    <button
                        onClick={() => navigate(isAcademic ? `/teacher/courses/${courseId}?tab=assignments` : '/teacher/clubs')}
                        className="px-6 py-2.5 bg-[#18216D] text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-indigo-900/10 hover:scale-[1.02] transition-all active:scale-95"
                    >
                        Return to {isAcademic ? 'Course' : 'Hub'}
                    </button>
                </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
                {/* Fixed Student Navigation Sidebar */}
                <div className="w-80 bg-white border-r border-slate-200 flex flex-col overflow-hidden hidden lg:flex shadow-inner">
                    <div className="p-6 border-b border-slate-100 bg-slate-50/30">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search scholars..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-[11px] font-bold text-[#18216D] focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 transition-all outline-none"
                            />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
                        {filteredStudents.map((sub) => (
                            <button
                                key={sub.student || sub.id}
                                onClick={() => setSelectedStudentId(sub.student)}
                                className={`w-full flex items-center gap-4 p-4 rounded-[1.25rem] transition-all group ${
                                    selectedStudentId === sub.student
                                        ? 'bg-indigo-50 border border-indigo-100 shadow-sm translate-x-1'
                                        : 'hover:bg-slate-50 border border-transparent'
                                }`}
                            >
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs transition-all shadow-sm ${
                                    selectedStudentId === sub.student
                                        ? 'bg-[#18216D] text-white'
                                        : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'
                                }`}>
                                    {sub.student_name?.charAt(0)}
                                </div>
                                <div className="text-left flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <p className={`text-[11px] font-black uppercase tracking-tight truncate ${
                                            selectedStudentId === sub.student ? 'text-[#18216D]' : 'text-slate-600'
                                        }`}>
                                            {sub.student_name}
                                        </p>
                                        {sub.status === 'graded' && (
                                            <CheckCircleIcon className="w-4 h-4 text-emerald-500 shrink-0" />
                                        )}
                                    </div>
                                    <div className="flex flex-col mt-1">
                                        <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">
                                            {sub.student_registration_id || sub.student_id || 'N/A'}
                                        </p>
                                        <p className="text-[9px] font-bold text-slate-400 mt-0.5">
                                            {sub.grade_level_name} • {sub.stream_name}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Main Content Area - 3 Sections Stacked */}
                <div className="flex-1 overflow-y-auto bg-slate-50 p-6 lg:p-10 custom-scrollbar scroll-smooth">
                    <div className="max-w-4xl mx-auto space-y-8 pb-32">
                                                {/* SECTION 1: RUBRIC & CRITERIA */}
                        <section className="bg-white rounded-[2.5rem] shadow-xl shadow-indigo-900/5 border border-slate-100 p-8 md:p-10 overflow-hidden relative group transition-all hover:border-indigo-100">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center border border-indigo-100">
                                        <ClipboardDocumentCheckIcon className="w-6 h-6 text-indigo-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-[16px] font-black text-[#18216D] uppercase tracking-widest italic">
                                            {selectedTemplate?.name || assignment?.title || 'Rubric Criteria'}
                                        </h2>
                                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-tight mt-0.5">
                                            {selectedTemplate?.description || assignment?.description || 'Defined performance criteria for this assessment'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {competencyLevels.map(level => {
                                    const criteria = selectedTemplate ? selectedTemplate[`criteria_${level.code.toLowerCase()}`] : assignment?.[`criteria_${level.code.toLowerCase()}`];
                                    return (
                                        <div key={level.code} className={`p-6 rounded-[2rem] border transition-all ${level.bg} ${level.border} group/card hover:scale-[1.01]`}>
                                            <div className="flex items-center justify-between mb-4">
                                                <span className={`text-3xl font-black italic ${level.text} opacity-40 group-hover/card:opacity-100 transition-opacity`}>{level.code}</span>
                                                <span className={`text-[9px] font-black uppercase tracking-[0.2em] ${level.text} opacity-70 underline underline-offset-4`}>{level.label}</span>
                                            </div>
                                            <p className={`text-[11px] font-bold leading-relaxed ${level.text} italic min-h-[44px]`}>
                                                {criteria || "No specific rubric text defined for this level."}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>

                        {/* SECTION 2: EVALUATION ENGINE */}
                        {selectedStudentId ? (
                            <section className="bg-white rounded-[2.5rem] shadow-xl shadow-indigo-900/5 border border-slate-100 overflow-hidden group transition-all hover:border-indigo-100">
                                <div className="p-8 md:p-10">
                                    {/* Student Card Header */}
                                    <div className="flex flex-col lg:flex-row items-center justify-between gap-8 mb-8 bg-slate-50/50 p-8 rounded-[2rem] border border-slate-100 outline outline-4 outline-transparent group-hover:outline-indigo-500/5 transition-all">
                                        <div className="flex items-center gap-6">
                                            <div className="w-20 h-20 bg-[#18216D] rounded-[1.5rem] flex items-center justify-center font-black text-white text-3xl shadow-2xl shadow-indigo-900/20 rotate-[-2deg] group-hover:rotate-0 transition-transform">
                                                {currentStudent?.student_name?.charAt(0)}
                                            </div>
                                            <div>
                                                <h3 className="text-2xl font-black text-[#18216D] tracking-tighter italic uppercase">{currentStudent?.student_name}</h3>
                                                <div className="flex items-center gap-3 mt-1.5">
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">ADM NO: {currentStudent?.student_registration_id || currentStudent?.student_id || 'N/A'}</span>
                                                    <span className="w-1.5 h-1.5 bg-slate-200 rounded-full" />
                                                    <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100">
                                                        {currentStudent?.grade_level_name} • {currentStudent?.stream_name}
                                                    </span>
                                                    <span className="w-1.5 h-1.5 bg-slate-200 rounded-full" />
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                        Submitted: {currentStudent?.submitted_at ? new Date(currentStudent.submitted_at).toLocaleDateString() : 'Manual Entry'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            {/* Live Score & Competency Badge */}
                                            {(() => {
                                                const totalMarks = parseFloat(assignment?.total_marks) || 100;
                                                const roundedMark = rawMarks[selectedStudentId] !== undefined && rawMarks[selectedStudentId] !== null && rawMarks[selectedStudentId] !== ''
                                                    ? Math.round(parseFloat(rawMarks[selectedStudentId]))
                                                    : '';
                                                const numericMark = roundedMark !== '' ? roundedMark : 0;
                                                const pct = totalMarks > 0 ? Math.round((numericMark / totalMarks) * 100) : 0;
                                                const levelCode = calculateLevel(numericMark, totalMarks, performanceBands);
                                                const levelObj = competencyLevels.find(l => l.code === levelCode) || competencyLevels[1];

                                                return (
                                                    <div className="flex items-center gap-4 bg-white p-4 rounded-2xl border-2 border-indigo-100 shadow-sm">
                                                        <div className="flex flex-col items-end">
                                                            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Score</span>
                                                            <div className="flex items-center gap-2">
                                                                <input 
                                                                    type="number"
                                                                    value={roundedMark}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        handleRawMarkChange(selectedStudentId, val !== '' ? Math.round(parseFloat(val)) : '');
                                                                    }}
                                                                    onBlur={() => {
                                                                        const mark = rawMarks[selectedStudentId];
                                                                        if (mark === undefined || mark === null || String(mark).trim() === '') return;
                                                                        const lvl = grades[selectedStudentId] || 'ME';
                                                                        saveGrade(selectedStudentId, lvl, mark, teacherComments[selectedStudentId]);
                                                                    }}
                                                                    onKeyDown={async (e) => {
                                                                        if (e.key === 'Enter' && !submitting) {
                                                                            const mark = rawMarks[selectedStudentId];
                                                                            const lvl = grades[selectedStudentId] || 'ME';
                                                                            const success = await saveGrade(selectedStudentId, lvl, mark, teacherComments[selectedStudentId]);
                                                                            if (success) {
                                                                                const idx = filteredStudents.findIndex(s => s.student === selectedStudentId);
                                                                                if (idx < filteredStudents.length - 1) {
                                                                                    setSelectedStudentId(filteredStudents[idx+1].student);
                                                                                } else {
                                                                                    showToast('End of scholars list reached! 🎉', 'success');
                                                                                }
                                                                            }
                                                                        }
                                                                    }}
                                                                    className="w-16 bg-transparent text-right font-black text-2xl text-[#18216D] outline-none placeholder:text-slate-200 transition-all focus:scale-110"
                                                                    placeholder="0"
                                                                    min="0"
                                                                    max={totalMarks}
                                                                    step="1"
                                                                />
                                                                <span className="text-xl font-black text-slate-300 italic">/</span>
                                                                <span className="text-xl font-black text-slate-400 italic">{Math.round(totalMarks)}</span>
                                                            </div>
                                                        </div>

                                                        <div className="w-px h-10 bg-slate-100" />

                                                        <div className="flex flex-col items-start pl-1">
                                                            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Rating</span>
                                                            <div className="flex items-center gap-2">
                                                                <span className={`px-3 py-1 rounded-xl text-xs font-black italic border shadow-sm ${levelObj.bg} ${levelObj.text} ${levelObj.border}`}>
                                                                    {levelCode} ({pct}%)
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>

                                    {/* Per-Strand Score Breakdown Deck */}
                                    {assignment?.tested_strands_detail && assignment.tested_strands_detail.length > 0 && (
                                        <div className="mt-8 bg-gradient-to-br from-indigo-50/40 via-white to-slate-50 p-6 md:p-8 rounded-[2rem] border border-indigo-100/80 shadow-lg shadow-indigo-900/5">
                                            <div className="flex items-center justify-between mb-6">
                                                <div>
                                                    <h4 className="text-xs font-black text-[#18216D] uppercase tracking-widest flex items-center gap-2">
                                                        <span>Tested Strands Breakdown</span>
                                                    </h4>
                                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Competency distribution across tested strands</p>
                                                </div>
                                                <span className="text-[10px] font-black px-3 py-1 bg-indigo-100/60 text-indigo-700 rounded-full uppercase tracking-wider">
                                                    {assignment.tested_strands_detail.length} Tested Strands
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 gap-4">
                                                {assignment.tested_strands_detail.map((strand, sIdx) => {
                                                    const totalMarks = parseFloat(assignment?.total_marks) || 100;
                                                    const strandThresholdObj = (assignment?.strand_thresholds || {})[strand.id] || {};
                                                    const strandMax = Math.round(parseFloat(strandThresholdObj.max) || (totalMarks / assignment.tested_strands_detail.length));

                                                    const studentStrands = strandScores[selectedStudentId] || {};
                                                    let strandScore = '';

                                                    if (studentStrands[strand.id] !== undefined && studentStrands[strand.id] !== null) {
                                                        strandScore = studentStrands[strand.id];
                                                    } else {
                                                        const currentMark = rawMarks[selectedStudentId] !== undefined && rawMarks[selectedStudentId] !== null && rawMarks[selectedStudentId] !== ''
                                                            ? Math.round(parseFloat(rawMarks[selectedStudentId]))
                                                            : null;
                                                        const pct = currentMark !== null && totalMarks > 0 ? (currentMark / totalMarks) : null;
                                                        strandScore = pct !== null ? Math.round(pct * strandMax) : '';
                                                    }

                                                    const numStrandScore = strandScore !== '' ? parseFloat(strandScore) : 0;
                                                    const strandPct = strandMax > 0 ? Math.round((numStrandScore / strandMax) * 100) : 0;
                                                    const strandLevel = calculateLevel(numStrandScore, strandMax, performanceBands);
                                                    const strandLevelObj = competencyLevels.find(l => l.code === strandLevel) || competencyLevels[1];

                                                    return (
                                                        <div key={strand.id || sIdx} className="group bg-white p-5 rounded-2xl border border-slate-100 hover:border-indigo-200 transition-all duration-300 shadow-sm hover:shadow-md">
                                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                                                <div className="flex items-start sm:items-center gap-3.5">
                                                                    <div className="w-9 h-9 bg-indigo-600 text-white rounded-xl flex items-center justify-center font-black text-sm shadow-md shadow-indigo-600/20 shrink-0">
                                                                        {sIdx + 1}
                                                                    </div>
                                                                    <div className="flex flex-col">
                                                                        <span className="text-xs font-black text-[#18216D] uppercase tracking-wide group-hover:text-indigo-600 transition-colors">
                                                                            {strand.name}
                                                                        </span>
                                                                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                                                                            Strand {sIdx + 1}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                <div className="flex items-center gap-3 self-end sm:self-auto">
                                                                    <div className="flex items-center space-x-2 bg-indigo-50/50 p-2 rounded-xl border border-indigo-100 shadow-inner">
                                                                        <input
                                                                            type="number"
                                                                            value={strandScore}
                                                                            onChange={(e) => handleStrandInputChange(strand.id, e.target.value, strandMax)}
                                                                            onBlur={() => {
                                                                                const mark = rawMarks[selectedStudentId];
                                                                                if (mark !== undefined && mark !== null && mark !== '') {
                                                                                    const lvl = grades[selectedStudentId] || 'ME';
                                                                                    saveGrade(selectedStudentId, lvl, mark, teacherComments[selectedStudentId]);
                                                                                }
                                                                            }}
                                                                            className="w-14 bg-white text-right font-black text-base text-[#18216D] px-2 py-1 border border-indigo-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500"
                                                                            placeholder="0"
                                                                            min="0"
                                                                            max={strandMax}
                                                                            step="1"
                                                                        />
                                                                        <span className="text-xs font-bold text-slate-400">/ {strandMax} Marks</span>
                                                                    </div>

                                                                    <span className={`px-3 py-1 rounded-xl text-xs font-black uppercase tracking-wider border shadow-sm ${strandLevelObj.bg} ${strandLevelObj.text} ${strandLevelObj.border}`}>
                                                                        {strandLevel} ({strandPct}%)
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* Live Strand Progress Bar */}
                                                            <div className="mt-3.5 w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                                                                <div 
                                                                    className="bg-gradient-to-r from-indigo-500 to-indigo-700 h-full rounded-full transition-all duration-500 shadow-sm"
                                                                    style={{ width: `${Math.min(100, Math.max(0, strandPct))}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </section>
                        ) : (
                            <div className="bg-white rounded-[2.5rem] border border-slate-100 border-dashed p-20 text-center">
                                <UserIcon className="w-16 h-16 text-slate-200 mx-auto mb-6" />
                                <h3 className="text-xl font-black text-[#18216D] uppercase italic">Select a Scholar</h3>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">Choose a student from the sidebar to begin EVALUATION</p>
                            </div>
                        )}

                        {/* SECTION 3: FEEDBACK & OBSERVATIONS */}
                        {selectedStudentId && (
                            <section className="bg-white rounded-[2.5rem] shadow-xl shadow-indigo-900/5 border border-slate-100 overflow-hidden group transition-all hover:border-indigo-100 animate-in slide-in-from-top-4 duration-700">
                                <div className="p-8 md:p-10">
                                    <div className="flex items-center gap-4 mb-8">
                                        <div className="w-12 h-12 bg-rose-50 rounded-2xl flex items-center justify-center border border-rose-100 rotate-[3deg]">
                                            <ChatBubbleBottomCenterTextIcon className="w-6 h-6 text-rose-600" />
                                        </div>
                                        <div>
                                            <h2 className="text-[14px] font-black text-[#18216D] uppercase tracking-widest italic">Scholar Feedback</h2>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Internal Observations & Report Comments</p>
                                        </div>
                                    </div>

                                    <div className="relative">
                                        <textarea 
                                            value={teacherComments[selectedStudentId] || ''}
                                            onChange={(e) => setTeacherComments(prev => ({ ...prev, [selectedStudentId]: e.target.value }))}
                                            onBlur={() => saveGrade(
                                                selectedStudentId, 
                                                grades[selectedStudentId], 
                                                rawMarks[selectedStudentId], 
                                                teacherComments[selectedStudentId],
                                                currentStudent?.marking_data
                                            )}
                                            className="w-full min-h-[220px] p-8 bg-slate-50 border-none rounded-[2rem] font-bold text-[#18216D] text-sm focus:ring-4 focus:ring-indigo-500/5 transition-all outline-none shadow-inner placeholder:text-slate-300 placeholder:italic"
                                            placeholder="Specify student performance, areas of improvement, or notable achievements for the report card..."
                                        />
                                        <div className="absolute top-6 right-6 p-2 bg-white/80 rounded-xl border border-slate-100">
                                            <ArrowPathIcon className={`w-4 h-4 text-slate-300 ${submitting ? 'animate-spin text-indigo-500' : ''}`} />
                                        </div>
                                    </div>
                                    
                                    <div className="mt-8 flex items-center justify-between">
                                        <div className="flex items-center gap-3 px-6 py-3 bg-[#18216D]/5 rounded-2xl border border-[#18216D]/10 text-[10px] font-black text-[#18216D] uppercase tracking-widest">
                                            <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                                            Evaluating: {currentStudent?.student_name}
                                        </div>
                                        
                                        <div className="group/nav flex items-center gap-2">
                                            <button 
                                                onClick={() => {
                                                    const idx = filteredStudents.findIndex(s => s.student === selectedStudentId);
                                                    if (idx > 0) setSelectedStudentId(filteredStudents[idx-1].student);
                                                }}
                                                className="px-6 py-3 bg-white border border-slate-200 rounded-xl text-[10px] font-black text-slate-600 uppercase tracking-widest hover:bg-slate-50 disabled:opacity-30 transition-all"
                                                disabled={filteredStudents.findIndex(s => s.student === selectedStudentId) === 0}
                                            >
                                                Back
                                            </button>
                                            <button 
                                                onClick={async () => {
                                                    try {
                                                        setIsMovingNext(true);
                                                        const success = await saveGrade(
                                                            selectedStudentId, 
                                                            grades[selectedStudentId], 
                                                            rawMarks[selectedStudentId], 
                                                            teacherComments[selectedStudentId],
                                                            currentStudent?.marking_data
                                                        );
                                                        
                                                        if (success) {
                                                            const idx = filteredStudents.findIndex(s => s.student === selectedStudentId);
                                                            if (idx < filteredStudents.length - 1) {
                                                                setSelectedStudentId(filteredStudents[idx+1].student);
                                                                // Scroll to top of evaluation section for next student
                                                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                                            } else {
                                                                showToast('End of assessment list reached', 'info');
                                                            }
                                                        }
                                                    } finally {
                                                        setIsMovingNext(false);
                                                    }
                                                }}
                                                disabled={isMovingNext || submitting}
                                                className="px-8 py-3 bg-[#18216D] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all shadow-lg flex items-center gap-2"
                                            >
                                                {isMovingNext || submitting ? (
                                                    <>
                                                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                                        Saving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <CheckCircleIcon className="w-4 h-4 text-emerald-400" />
                                                        Grade & Next
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        )}

                    </div>
                </div>
            </div>

            {/* Rubric Forge Modal (Screenshot 3 Style) */}
            {isForgingTemplate && (
                <BlueprintCreator
                    assignmentContext={assignment}
                    onCancel={() => setIsForgingTemplate(false)}
                    onSuccess={(newTemplate) => {
                        // Add to list and immediately select it
                        setTemplates(prev => {
                            const exists = prev.find(t => t.id === newTemplate.id);
                            if (exists) return prev;
                            return [newTemplate, ...prev];
                        });
                        setSelectedTemplate(newTemplate);
                        setIsForgingTemplate(false);
                        showToast('Template auto-selected for current grading session! ✨');
                    }}
                />
            )}

            <ConfirmModal
                isOpen={!!pendingTemplate}
                onClose={() => setPendingTemplate(null)}
                onConfirm={handleTemplateConfirm}
                title="Change Assessment Template?"
                message={`Are you sure you want to change the assessment template to "${pendingTemplate?.name}"? This will update the default rubric assigned to this assessment.`}
                loading={isUpdatingTemplate}
            />
        </div>
    );
};

export default GradeAssessmentPage;
